import { AfterViewInit, Component, ElementRef, inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { debounce } from 'lodash-es';
import { combineLatest, forkJoin, lastValueFrom, merge, Observable, ReplaySubject } from 'rxjs';
import { delay, filter, map, take, takeUntil } from 'rxjs/operators';

import { MatSelectChange } from '@angular/material/select';
import { Title } from '@angular/platform-browser';
import { allEmployeeRoles, AvailableLanguages, roleToPath } from '@constants';
import { WebsocketService } from '@core/services/websocket.service';
import { environment } from '@environment';
import { AllDeviceInfo, GUIDE_NAME, InfoBarState, MaintenanceInfo, ROLES_KEYS, User, UserProcess } from '@interfaces';
import { TranslateService } from '@ngx-translate/core';
import { InAppNotificationsFacade } from '@state/notifications';
import { RouterFacade } from '@state/router';
import { UserAuthFacade } from '@state/user-auth';
import { UserProcessFacade } from '@state/user-process';
import { LayoutService } from './layout.service';
import { LayoutScrollService } from './layout-scroll.service';
import { MenuItem, foreignerWorkPermitMenuItems, sideMenuConfig } from './side-menu.config';

import { SessionTimeoutService } from '@core/services/session-timeout.service';
import { AnalyticsService } from '@core/services/analytics.service';
import { DeviceInfoService } from '@core/services/device-info.service';
import { MobileVisualViewportService } from '@core/services/mobile-visual-viewport.service';
import { MyPartnerAccountDrawerService } from '@shared/my-partner-account-drawer/my-partner-account-drawer.service';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { SystemSettingsFacade } from '@state/system-settings/system-settings.facade';
import { MyWorkPermitsFacade } from '@state/my-work-permits';
import {
  foreignerApplicationLinkParams,
  isWorkPermitApplication,
  mostRecentForeignerApplication,
  WorkPermitApplication,
} from '@core/foreigner-applications-utils';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  providers: [LayoutScrollService],
  standalone: false,
})
export class LayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly userAuthFacade = inject(UserAuthFacade);
  private readonly userProcessFacade = inject(UserProcessFacade);
  private readonly routerFacade = inject(RouterFacade);
  private readonly router = inject(Router);
  private readonly layoutService = inject(LayoutService);
  private readonly layoutScrollService = inject(LayoutScrollService);
  private readonly inAppNotifications = inject(InAppNotificationsFacade);
  private readonly websocket = inject(WebsocketService);
  private readonly titleService = inject(Title);
  private readonly translateService = inject(TranslateService);
  private readonly sessionTimeout = inject(SessionTimeoutService);
  private readonly deviceInfoService = inject(DeviceInfoService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly myPartnerAccountDrawer = inject(MyPartnerAccountDrawerService);
  private readonly mobileVisualViewportService = inject(MobileVisualViewportService);
  private readonly ngZone = inject(NgZone);
  private readonly systemSettingsFacade = inject(SystemSettingsFacade);
  private readonly myWorkPermitsFacade = inject(MyWorkPermitsFacade);

  public myself$: Observable<User | null> = this.userAuthFacade.myself$;
  public isAdministrator = false;
  public isForeigner = false;
  public isEmployer = false;
  public myProcesses$ = this.userProcessFacade.myProcesses$;
  public myProcess$ = this.userProcessFacade.myProcess$;
  public myWorkPermits$ = this.myWorkPermitsFacade.myWorkPermits$;
  public hasApplications$ = combineLatest([this.myProcesses$, this.myWorkPermits$]).pipe(
    map(([myProcesses, myWorkPermits]) => !!myProcesses?.length || !!myWorkPermits?.length)
  );
  public maintenanceInfo$: Observable<MaintenanceInfo> = this.inAppNotifications.maintenanceInfo$;
  public isInSummary = false;

  @ViewChild('contentWrapper') contentWrapper: ElementRef;
  @ViewChild('drawerFocus') drawerFocus!: ElementRef;

  public showTopbarShadow = false;
  public showTopbarMenu = false;

  private lastFocusedElement: HTMLElement;

  public sideMenuItems: MenuItem[] = [];
  public myProcess: UserProcess;
  // Set while a foreigner is on `/my-work-permit/:id`.
  public selectedWorkPermitId: string | null = null;
  private foreignerUserProcessMenuItems: MenuItem[] = [];
  private foreignerWorkPermitMenuItems: MenuItem[] = [];

  public showProgressBar = false;
  public progressPercentage = 0;
  public showBottomFixedBar = false;

  public showOpenChatButton = true;

  public showNotifications = false;
  public unreadNotificationsCount = 0;
  public newNotificationReceived = false;

  public infoBarState: InfoBarState = { show: false, type: 'info' };

  private titleBlinkingInterval: any;

  private destroy$: ReplaySubject<boolean> = new ReplaySubject(1);
  private contentScrollHandler: (((event: Event) => void) & { cancel?: () => void }) | null = null;

  private PAGE_TITLE = environment.PAGE_TITLE;

  public foreignerMenuItems: any[] = [];
  public addingNewProcess: null | { id: string } = null;
  public GUIDE_NAME = GUIDE_NAME;
  public currentLang: AvailableLanguages;

  public userShouldSeeChatsList = false;

  public showSidebar = false;
  public sidebarWidth = '95vw';
  public deviceInfo: AllDeviceInfo;
  public allowedToParticipateInPartnerProgram: boolean;
  public partnerAccountDrawerRef: NzDrawerRef;

  ngOnInit(): void {
    this.deviceInfo = this.deviceInfoService.getInfo();
    this.mobileVisualViewportService.activate();

    this.deviceInfoService.infoEmitter.pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.deviceInfo = info;
      if (this.deviceInfo.deviceTypeDetected === 'DESKTOP') {
        this.toggleSidebar(false);
      }
    });
    this.startSubscribeRouter();

    this.sessionTimeout.startIdleWatch();
    this.layoutService.setupGuideDefaultOpts();

    this.currentLang = this.translateService.currentLang as AvailableLanguages;
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(({ lang }) => {
      this.currentLang = lang as AvailableLanguages;
    });

    this.userProcessFacade.myProcess$
      .pipe(
        filter(myProcess => !!myProcess?.id),
        takeUntil(this.destroy$)
      )
      .subscribe(myProcess => {
        this.myProcess = myProcess;
        this.addingNewProcess = null;
      });

    // We have to get 'myself' data
    // As well as 'modules-configuration'
    // This way we can setup side-moenu properly

    forkJoin({
      myself: this.myself$.pipe(
        filter(myself => !!myself?.role?.key),
        take(1),
        takeUntil(this.destroy$)
      ),
      modulesConfig: this.systemSettingsFacade.modulesConfiguration$.pipe(
        filter(config => !!config),
        take(1),
        takeUntil(this.destroy$)
      ),
    }).subscribe(({ myself: user, modulesConfig }) => {
      this.inAppNotifications.getMaintenanceInfo$().pipe(take(1), takeUntil(this.destroy$)).subscribe();
      this.userAuthFacade.getBranding$().pipe(take(1), takeUntil(this.destroy$)).subscribe();

      const currentUserRoleKey = user.role.key as ROLES_KEYS;
      this.isAdministrator = currentUserRoleKey === ROLES_KEYS.Admin;
      this.isForeigner = currentUserRoleKey === ROLES_KEYS.Foreigner;
      this.isEmployer = currentUserRoleKey === ROLES_KEYS.Employer;

      const sideMenuItems = sideMenuConfig[currentUserRoleKey];
      const isMenuItemEnabled = (item: MenuItem): boolean => {
        if (!item.moduleKey) {
          return false;
        }

        // modules are enabled by default
        // if given module is not present in the config fetched from API
        // then it's enabled
        const moduleConfigManagedByBackend = modulesConfig[item.moduleKey] !== undefined;
        const isModuleEnabled = moduleConfigManagedByBackend ? modulesConfig[item.moduleKey] : true;

        return isModuleEnabled;
      };
      this.sideMenuItems = sideMenuItems.filter(isMenuItemEnabled);

      if (this.isForeigner) {
        this.foreignerUserProcessMenuItems = this.sideMenuItems;
        this.foreignerWorkPermitMenuItems = foreignerWorkPermitMenuItems.filter(isMenuItemEnabled);
        this.updateForeignerView();
      }

      const rolesAllowedToParticipate = [ROLES_KEYS.Foreigner];
      this.allowedToParticipateInPartnerProgram =
        rolesAllowedToParticipate.includes(currentUserRoleKey) && user.company?.purchaseEnabled;

      // show support-chat for these roles
      this.userShouldSeeChatsList = allEmployeeRoles.includes(user.role.key as ROLES_KEYS);

      if (this.isForeigner) {
        // if there is no userProcess picked already - reroute to the first one - if exists
        // if does not exist - reroute to the userProcess creation

        forkJoin({
          myProcesses: this.userProcessFacade.getMyProcesses$().pipe(take(1)),
          myWorkPermits: this.myWorkPermitsFacade.getMyWorkPermits$().pipe(take(1)),
        })
          .pipe(takeUntil(this.destroy$))
          .subscribe(({ myProcesses, myWorkPermits }) => {
            const hasProcesses = !!myProcesses?.length;
            const canAddOwnProcesses = user?.allowedToAddOwnUserProcesses;
            const processesLImit = user?.userProcessesLimit;

            if (this.router.url.includes('user-process-form')) {
              if (canAddOwnProcesses && hasProcesses && myProcesses.length < processesLImit) {
                this.goToApplication({ id: 'new' } as UserProcess);
              } else {
                this.goToApplication({ id: 'purchase' } as UserProcess);
              }
            }

            if (this.router.url.includes('purchase')) {
              // User came back from payment if url includes `:purchaseId/status`
              // In this case we should not make any redirects (inside .goToApplication)
              // thus we're checking here if this is products-selection or after-payment page
              // if there is no 'status' in url -> this is products-selection
              const shouldChangeRoute = !this.router.url.includes('status?backFromOperator=true');
              this.goToApplication({ id: 'purchase' } as UserProcess, shouldChangeRoute);
            }

            if (this.router.url.length > 1 && this.router.url !== '/user-process') {
              return;
            }

            // user is in blank view
            if (this.router.url.length <= 1 || this.router.url === '/user-process') {
              if (myWorkPermits.length) {
                this.goToApplication(mostRecentForeignerApplication(myProcesses ?? [], myWorkPermits));
                return;
              }

              let appToGo = hasProcesses ? myProcesses[0] : ({ id: 'new' } as UserProcess);
              if (!hasProcesses && canAddOwnProcesses) {
                appToGo = { id: 'new' } as UserProcess;
              }
              if (!hasProcesses && !canAddOwnProcesses) {
                appToGo = { id: 'purchase' } as UserProcess;
              }
              if (hasProcesses) {
                appToGo = myProcesses.reduce((recent, p) =>
                  new Date(p.updatedAt) > new Date(recent.updatedAt) ? p : recent
                );
              }
              if (appToGo.id === 'purchase' && !user.company?.purchaseEnabled) {
                this.routerFacade.changeRoute({ linkParams: ['/no-processes-available'] });
                return;
              }

              this.goToApplication(appToGo);
            }
          });
      }

      // ---- SHOULD RUUN ONLY ONCE ON LAYOUT INIT!!!
      this.websocket.init();
      this.listenForLayoutChanges();
      this.setupNotifications();
    });

    this.myself$
      .pipe(
        filter(user => !!user?.role),
        takeUntil(this.destroy$)
      )
      .subscribe(user => {
        const currentUserRoleKey = user.role.key as ROLES_KEYS;
        /**
         * if router.url.length === 1 - we should redirect user to some of the views
         * if router.url.length > 1  - user has already correct URL and no redirects
         *
         * this is only for cases where user enters the app
         * - and is already logged-in
         * - and did not provide url with path
         */
        const urlLength = this.router.url.length;
        if (urlLength === 1) {
          const path = roleToPath[currentUserRoleKey];
          this.routerFacade.changeRoute({ linkParams: [path] });
          return;
        }
      });
  }

  ngAfterViewInit(): void {
    const htmlElem = this.contentWrapper?.nativeElement as HTMLElement;
    this.layoutScrollService.setContentElement(htmlElem);
    this.removeContentScrollListener();

    this.contentScrollHandler = debounce((event: Event) => {
      const { scrollTop } = event.target as HTMLElement;
      this.showTopbarShadow = scrollTop > 0;
    }, 10);

    // making sure that it exists
    // if not - do not interrupt user - this is only some fancy shadow below topbar
    if (htmlElem.addEventListener) {
      htmlElem.addEventListener('scroll', this.contentScrollHandler);
    }
  }

  ngOnDestroy(): void {
    this.removeContentScrollListener();
    this.layoutScrollService.destroy();
    this.mobileVisualViewportService.deactivate();

    if (this.partnerAccountDrawerRef?.nzClosable) {
      this.partnerAccountDrawerRef.close();
    }
    this.websocket.close();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public openGuide(guideName: GUIDE_NAME): void {
    if (this.deviceInfo.deviceTypeDetected === 'DESKTOP') {
      this.layoutService
        .startGuide(guideName, this.translateService)
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe(() => {});
    }
  }

  public goToApplication(app: UserProcess | WorkPermitApplication, shouldChangeRoute: boolean = true): void {
    const currActive = this.foreignerMenuItems.find(({ active }) => active === true);
    if (currActive) {
      currActive.active = false;
    }

    // The user-process selection is kept, so it is restored when leaving the work-permit.
    if (isWorkPermitApplication(app)) {
      if (shouldChangeRoute) {
        this.routerFacade.changeRoute({ linkParams: foreignerApplicationLinkParams(app) });
      }
      return;
    }

    if (app.id === 'new') {
      this.addingNewProcess = { id: 'new' };
      this.myProcess = { id: 'new' } as UserProcess;
      if (shouldChangeRoute) {
        this.routerFacade.changeRoute({ linkParams: ['/user-process-form'] });
      }
      return;
    }
    if (!app?.id || app.id === 'purchase') {
      this.addingNewProcess = { id: 'purchase' };
      this.myProcess = { id: 'purchase' } as UserProcess;
      if (shouldChangeRoute) {
        this.routerFacade.changeRoute({ linkParams: ['/purchase'] });
      }
      return;
    }

    this.addingNewProcess = null;
    if (shouldChangeRoute) {
      this.routerFacade.changeRoute({
        linkParams: ['/user-process', app.id],
        extras: {},
      });
    }
  }

  public applicationChanged({ value }: MatSelectChange): void {
    this.toggleSidebar(false);
    this.analyticsService.trackEvent('user_event', 'user_changed_application');
    this.goToApplication(value);
  }

  public toggleNotificationsWindow(): void {
    this.disableTitleBlinking(this.PAGE_TITLE);
    const nextState = !this.showNotifications;

    this.showNotifications = nextState;

    if (nextState) {
      this.showTopbarMenu = false;
    }
  }

  public toggleTopbarMenu(): void {
    const nextState = !this.showTopbarMenu;

    this.showTopbarMenu = nextState;

    if (nextState) {
      this.showNotifications = false;
    }
  }

  public infoBarClicked(): void {
    const action = this.infoBarState.action;
    if (action === 'CALLBACK') {
      this.infoBarState.callback();
    }
  }

  public toggleSidebar(value?: boolean): void {
    const nextState = value !== undefined ? value : !this.showSidebar;

    if (nextState === this.showSidebar) {
      return;
    }

    if (nextState === true) {
      this.lastFocusedElement = document.activeElement as HTMLElement;
      this.showSidebar = true;
      return;
    }

    this.showSidebar = false;

    queueMicrotask(() => {
      this.lastFocusedElement?.focus();
    });
  }

  public onDrawerVisibilityChange(visible: boolean): void {
    if (visible === false) {
      return;
    }

    this.ngZone.onStable.pipe(take(1)).subscribe(() => {
      this.drawerFocus?.nativeElement.focus();
    });
  }

  public startSubscribeRouter() {
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationStart),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.toggleSidebar(false);
      });

    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.isInSummary = this.router.url.includes('/summary/');
        this.updateForeignerView();
      });
  }

  // Foreigner's side menu depends on what is displayed: work-permit, purchase (empty) or user-process.
  private updateForeignerView(): void {
    if (!this.isForeigner) {
      return;
    }

    const [path] = this.router.url.split('?');
    const [, section, workPermitId] = path.split('/');
    this.selectedWorkPermitId = section === 'my-work-permit' && workPermitId ? workPermitId : null;

    if (this.selectedWorkPermitId) {
      this.sideMenuItems = this.foreignerWorkPermitMenuItems;
    } else if (section === 'purchase') {
      this.sideMenuItems = [];
    } else {
      this.sideMenuItems = this.foreignerUserProcessMenuItems;
    }
  }

  public async logoClicked(): Promise<void> {
    const roleKey = await lastValueFrom(
      this.myself$.pipe(
        filter(myself => !!myself?.role?.key),
        take(1),
        map(myself => myself.role.key),
        takeUntil(this.destroy$)
      )
    );

    if (roleKey === ROLES_KEYS.Foreigner) {
      return await this.foreignerLogoClicked();
    }

    const applicationsListViewForRoles = [ROLES_KEYS.Team_Leader, ROLES_KEYS.Employee, ROLES_KEYS.Admin];

    if (
      applicationsListViewForRoles.includes(roleKey as ROLES_KEYS) &&
      !this.router.url.includes('management/applications-list')
    ) {
      this.routerFacade.changeRoute({ linkParams: ['/management/applications-list'] });
      return;
    }

    if (roleKey === ROLES_KEYS.Field_Consultant && !this.router.url.includes('management/visits-list')) {
      this.routerFacade.changeRoute({ linkParams: ['/management/visits-list'] });
      return;
    }

    if (roleKey === ROLES_KEYS.Employer && !this.router.url.includes('manager/company-employees-list')) {
      this.routerFacade.changeRoute({ linkParams: ['/manager/company-employees-list'] });
      return;
    }
  }

  public openMyPartnerAccountDrawer(): void {
    this.myself$.pipe(take(1), takeUntil(this.destroy$)).subscribe(myself => {
      if (!this.allowedToParticipateInPartnerProgram) {
        return;
      }

      this.partnerAccountDrawerRef = this.myPartnerAccountDrawer.open({
        userName: myself.name,
      });
    });
  }

  private setupNotifications(): void {
    this.inAppNotifications.unreadCount$
      .pipe(
        filter(({ unreadCount }) => this.unreadNotificationsCount !== unreadCount),
        takeUntil(this.destroy$)
      )
      .subscribe(({ unreadCount }) => {
        this.unreadNotificationsCount = unreadCount;
        if (this.unreadNotificationsCount === 0) {
          this.disableTitleBlinking(this.PAGE_TITLE);
        }
      });

    merge(
      this.inAppNotifications.markAsUnreadSuccess$,
      this.inAppNotifications.markAsReadSuccess$,
      this.inAppNotifications.markAllAsReadSuccess$,
      this.inAppNotifications.markAllOfTypeAsReadSuccess$,
      this.inAppNotifications.newNotificationReceived$
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.inAppNotifications.getUnreadCount();
      });

    this.inAppNotifications.newNotificationReceived$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.newNotificationReceived = true;

      // start blinking card-title only when notificaitons window is closed
      if (!this.showNotifications) {
        const translation = this.translateService.instant('IN_APP_NOTIFICATIONS.YOU_HAVE_NEW_NOTIFICATION');
        this.enableTitleBlinking(this.PAGE_TITLE, translation);
      }

      setTimeout(() => {
        this.newNotificationReceived = false;
      }, 1000);
    });

    this.inAppNotifications.getUnreadCount$().pipe(take(1), takeUntil(this.destroy$)).subscribe();
  }

  private listenForLayoutChanges(): void {
    this.layoutService.showProgressBar$
      .asObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe(val => {
        setTimeout(() => {
          this.showProgressBar = val;
        }, 50);
      });

    this.layoutService.progressPercentage$
      .asObservable()
      .pipe(
        filter(val => val !== this.progressPercentage),
        takeUntil(this.destroy$)
      )
      .subscribe(val =>
        setTimeout(() => {
          this.progressPercentage = val;
        }, 50)
      );

    this.layoutService.showBottomFixedBar$
      .asObservable()
      .pipe(
        filter(val => val !== this.showBottomFixedBar),
        takeUntil(this.destroy$)
      )
      .subscribe(val => {
        setTimeout(() => {
          this.showBottomFixedBar = val;
        }, 50);
      });

    this.layoutService.showOpenChatButton$
      .asObservable()
      .pipe(
        filter(val => val !== this.showOpenChatButton),
        takeUntil(this.destroy$)
      )
      .subscribe(val => {
        setTimeout(() => {
          this.showOpenChatButton = val;
        }, 50);
      });

    this.layoutService.infoBarState$
      .asObservable()
      .pipe(delay(1), takeUntil(this.destroy$))
      .subscribe(infoBarState => {
        setTimeout(() => {
          this.infoBarState = { ...infoBarState };
        });
      });

    this.layoutService.scrollContentTo$
      .asObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe(opts => {
        setTimeout(() => {
          this.layoutScrollService.scrollContentTo(opts);
        }, 50);
      });

    this.layoutService.scrollIntoView$
      .asObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe(opts => {
        setTimeout(() => {
          const elToScrollTo = opts.htmlElem || (opts.htmlElemID ? document.getElementById(opts.htmlElemID) : null);
          if (!elToScrollTo) {
            console.warn('Unable to scroll content because elem does not exist or method scrollTo is unavailable');
            return;
          }

          this.layoutScrollService.scrollElementIntoContent(
            elToScrollTo,
            opts.block || 'center',
            opts.behavior || 'smooth'
          );
        }, 50);
      });
  }

  private removeContentScrollListener(): void {
    const htmlElem = this.contentWrapper?.nativeElement as HTMLElement;

    if (htmlElem?.removeEventListener && this.contentScrollHandler) {
      htmlElem.removeEventListener('scroll', this.contentScrollHandler);
    }

    this.contentScrollHandler?.cancel?.();
    this.contentScrollHandler = null;
  }

  private enableTitleBlinking(val1: string, val2: string): void {
    if (this.titleBlinkingInterval) {
      clearInterval(this.titleBlinkingInterval);
      this.titleBlinkingInterval = null;
    }

    this.titleBlinkingInterval = setInterval(() => {
      const currentTitle = this.titleService.getTitle();
      const titleToSet = currentTitle === val1 ? val2 : val1;
      this.titleService.setTitle(titleToSet);
    }, 1500);
  }

  private disableTitleBlinking(titleToSet: string): void {
    if (this.titleBlinkingInterval) {
      clearInterval(this.titleBlinkingInterval);
      this.titleBlinkingInterval = null;
    }

    this.titleService.setTitle(titleToSet);
  }

  private async foreignerLogoClicked(): Promise<void> {
    // if foreigner is at route: user-process/[userProcessId] or my-work-permit/[workPermitId] -> do nothing
    if (this.router.url.includes('user-process') || this.selectedWorkPermitId) {
      return;
    }

    // if foreigner is somewhere else (settings / purchase) -> navigate to first application in collection of his applications
    const myProcesses = await lastValueFrom(
      this.myProcesses$.pipe(
        filter(myProcesses => myProcesses !== null),
        take(1),
        takeUntil(this.destroy$)
      )
    );
    const myWorkPermits = await lastValueFrom(
      this.myWorkPermitsFacade.getMyWorkPermits$().pipe(take(1), takeUntil(this.destroy$))
    );

    if (myWorkPermits?.length) {
      this.goToApplication(mostRecentForeignerApplication(myProcesses ?? [], myWorkPermits));
      return;
    }

    if (myProcesses?.length) {
      this.goToApplication(myProcesses[0]);
      return;
    }

    const myself = await lastValueFrom(
      this.myself$.pipe(
        filter(user => !!user),
        take(1),
        takeUntil(this.destroy$)
      )
    );
    if (!myself.company?.purchaseEnabled) {
      this.routerFacade.changeRoute({ linkParams: ['/no-processes-available'] });
      return;
    }

    // this is the case where foreigner does not have any application(process) started
    // he/she must go to the purchase to buy atleast 1.
    this.goToApplication({ id: 'purchase' } as UserProcess, true);
  }

  public skipToMain(event: Event): void {
    event.preventDefault();

    const progress = document.querySelector<HTMLElement>('.mobile-percentages-container[role="progressbar"]');

    if (progress) {
      progress.focus();
      return;
    }

    const skipTarget = document.querySelector<HTMLElement>('[data-skip-target]');

    if (skipTarget) {
      skipTarget.focus();
      return;
    }

    const main = document.getElementById('main-content');

    if (main) {
      main.focus();
    }
  }
}
